import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

import { ATOMS_TYPOGRAPHY_CLASSES } from '../styles/typography.generated';

/**
 * Atoms typography utilities (`text-{family}-{size}`) come from the `--text-*`
 * theme tokens. tailwind-merge's default config doesn't know these custom
 * values, so its greedy `text-color` group swallows them — making
 * `cn('text-od-content-primary', 'text-display-lg')` drop the color (the two
 * land in the same conflict group). Registering them as explicit members of the
 * `font-size` group keeps typography and text-color in separate groups: exact
 * class matches are resolved before the `text-color` validator, so
 * `text-display-lg` → font-size and `text-od-*` → text-color.
 *
 * The class list is generated from `THEME_CONFIG.typography` (the source of
 * truth) by `pnpm generate:themes` → `styles/typography.generated.ts`, so it
 * stays in sync with the ramp automatically.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: [...ATOMS_TYPOGRAPHY_CLASSES] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const toHex = (n: number) => n.toString(16).padStart(2, '0');

export function safeRandomId(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  // RFC4122 v4 using getRandomValues if available
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, toHex).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
      12,
      16,
    )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  // Last-resort fallback
  return `r${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}
